(ns admin.containers.activity.boosts
  (:require
   [clojure.string :as str]
   [re-frame.core :as re-frame]

   [kyber.components.base.text :as text]
   [kyber.components.composite.loading :as loading]
   [kyber.styles.core :refer [gs styles->classes]]
   [kyber.utils :as ku]
   [kyber.components.base.link :as link]

   [admin.utils :as au]
   [admin.router.utils :as aru]
   [admin.components.forms.activity :refer [content-container-styles]]
   [admin.components.composite.is-live-pill :as is-live-pill]
   [admin.components.composite.learning-objective-pill :as lo-pill]
   [kyber.lib.kyber-db :as kyber-db]
   [reagent.core :as r]))

(def classes
  (styles->classes
   {:container
    {:padding (gs [:spacing :p20])
     "& > *"
     {:margin-bottom (gs [:spacing :p40])}}

    :activity-card
    {:box-shadow (gs [:shadows :shadow-12])
     :padding (gs [:spacing :p20])
     :max-width "1000px"
     :margin :auto
     :border-radius (gs [:radius :r7])
     :margin-bottom (gs [:spacing :p40])}

    :title-container
    {:display :flex
     :align-items :center}

    :reengagement-boost-message
    {:max-width "600px"
     :margin [(gs [:spacing :p20]) :auto]
     :color (gs [:colors :gray-scale-400])}

    :boost-list-header
    {:display :flex
     :justify-content :space-between
     :align-items :flex-end
     :margin-bottom (gs [:spacing :p20])}

    :boost-card-container
    {:border (str "1px solid " (gs [:colors :gray-scale-200]))
     :background-color (gs [:colors :gray-scale-50])
     :padding [(gs [:spacing :p8])
               (gs [:spacing :p20])]
     :border-radius (gs [:radius :r3])
     :margin-bottom (gs [:spacing :p32])}

    :boost-card-title-container
    {:display :flex
     :justify-content :space-between
     :align-items :center}

    :publish-details-container
    {:display :flex
     :align-items :center

     "& > *:first-child"
     {:margin-right (gs [:spacing :p20])}}

    :processing-stats-container
    {:display :flex
     :align-items :left

     "& > *"
     {:margin-right (gs [:spacing :p4])}}

    :tag-group-container
    {:align-items :left
     :margin-right (gs [:spacing :p32])
     :margin-bottom (gs [:spacing :p16]) 

     "& > *"
     {:margin-right (gs [:spacing :p4])}}

    :boost-card-name-line
    {:display :flex
     :align-items :center}

    :boost-details-line
    {:display :flex
     :justify-content :space-between
     :align-items :flex-end}

    :boost-feature-toggle-container
    {:display :flex
     :align-items :center

     "& > *"
     {:margin-right (gs [:spacing :p8])

      "&:after"
      {:content "\"|\""
       :margin-left (gs [:spacing :p8])
       :color (gs [:colors :gray-scale-200])}

      "&:last-child"
      {"&:after"
       {:display :none}}}}

    :boost-section-list-container
    {:width "100%"}

    :email-stat-container
    {:display :flex
     :align-items :center
     :border (str "1px solid " (gs [:colors :gray-scale-200]))
     :background-color (gs [:colors :true-white])
     :padding [(gs [:spacing :p0])
               (gs [:spacing :p20])]
     :margin-right (gs [:spacing :p12])}

    :click-rate-and-cta-container
    {:display :flex
     :align-items :center}
    :empty-boost-section-container
    {:display :flex
     :flex-direction :column
     :justify-content :center
     :align-items :center
     :margin-bottom (gs [:spacing :p40])
     :padding (gs [:spacing :p20])
     :background-color (gs [:colors :gray-scale-50])
     :border (str "1px solid " (gs [:colors :gray-scale-200]))
     :max-width "500px"
     :margin :auto
     :border-radius (gs [:radius :r3])}
    :empty-boost-section-title
    {:text-align :center
     :margin-bottom (gs [:spacing :p12])
     :color (gs [:colors :gray-scale-400])}

    :empty-boost-section-message
    {:color (gs [:colors :gray-scale-400])
     :margin-bottom (gs [:spacing :p20])}

    :admin-content-container
    (merge content-container-styles
           {:border (str "1px solid " (gs [:colors :error-500]))
            :background-color "rgba(255,0,0,0.1)"
            :flex-direction :column
            :align-items :start})
    :admin-content-title
    {:margin-bottom (gs [:spacing :p40])}

    :show-more-show-less-button
    {:font-family (gs [:font-family :open-sans])
     :margin-top (gs [:spacing :p4])
     :cursor "pointer"
     :background "none"
     :border "none"
     :color (gs [:colors :primary-lite-600])
     :text-decoration "underline"}}))

(defn- seconds->time-str
  [seconds]
  (let [floor js/Math.floor
        days (floor (/ seconds (* 60 60 24)))
        hours (floor (/ (mod seconds (* 60 60 24)) (* 60 60)))
        minutes (floor (/ (mod seconds (* 60 60)) 60))]
    (str/join
     " "
     [(when (pos? days)
        (str days " " (ku/pluralize "day" days)))
      (when (pos? hours)
        (str hours " " (ku/pluralize "hour" hours) " "))
      (when (pos? minutes)
        (str minutes " " (ku/pluralize "minute" minutes)))])))

(defn- render-boost-header
  []
  [:div {:class (:title-container classes)}
   [text/title-30 :h4
    {:style {:margin-right (gs [:spacing :p16])}}
    "Activity Boosts"]

   [:span {:class "fa-stack fa-2x"}
    [:i {:class "fas fa-circle fa-stack-2x"
         :style {:color (gs [:colors :gray-scale-200])}}]
    [:i {:class "fas fa-envelope fa-stack-1x fa-inverse"
         :style {:color (gs [:colors :true-white])}}]]])

(defn- render-boost-description
  [is-last]
  [text/caption-20 :p
   {:style {:color (gs [:colors :gray-scale-400])
            :margin-top (gs [:spacing :p20])
            :margin-bottom (if (true? is-last) (gs [:spacing :p40]) 0)}}
   (str "Boosts are critical when it comes to re-engaging learners with "
        "content that will advance their understanding of the activity.")])

(defn- render-admin-reengagement-copy [_]
  [text/caption-10 :p
   {:style {:color (gs [:colors :gray-scale-400])
            :margin-bottom (gs [:spacing :p40])
            :font-style :italic}}
   (str "In addition to boosts you create, learners will receive "
        "re-engagement emails if they have started (but not completed) "
        "the activity.")])

(defn- render-new-boost-button [{:keys [title curriculum-uuid activity-uuid]}]
  [link/primary-anchor-button {:data-test "create-new-boost-button"
                               :href (str (au/->admin-url
                                           {:curriculum-uuid curriculum-uuid
                                            :activity-uuid activity-uuid})
                                          "/boosts/new")}
   title])

(defn- render-boost-list-header
  [{:keys [activity-uuid curriculum-uuid]}]
  [:div {:class (:boost-list-header classes)}
   [text/caption-30 :p
    {:style {:margin-bottom (gs [:spacing :p0])}}
    "Boosts are in order of when they will be delivered to learners."]
   [render-new-boost-button {:title "Add New Activity Boost"
                             :curriculum-uuid curriculum-uuid
                             :activity-uuid activity-uuid}]])

(defn- render-feature-toggle
  [{:keys [label enabled]}]
  [text/caption-10 :p
   {:style {:color (gs [:colors :gray-scale-400])}}
   (str label ": " (if (true? enabled) "Enabled" "Disabled"))])

(defn- render-email-stat
  [{:keys [label stat total]}]
  (let [{:keys [users]} stat]
    [:div {:class (:email-stat-container classes)}
     [text/title-10 :p {:style {:margin-right (gs [:spacing :p8])}} label]
     [text/title-30 :p
      (if (some? stat)
        (str (ku/calc-percent-rounded total users) "%")
        "No data")]]))

(defn extract-tag-groups [tags]
  (mapv (fn [tag-group]
          (mapv :content-provider.tag/name (:content-provider.tag-group/values tag-group)))
        tags))

(defn filter-tag-groups
  [tag-groups tag-filter]
  (map (fn [group]
         (let [group-uuid (:content-provider.tag-group/uuid group)
               allowed-tags (->> (get tag-filter (keyword group-uuid) [])
                                 (map :tag-id)
                                 (set))
               filtered-values (->> (:content-provider.tag-group/values group)
                                    (filter #(contains? allowed-tags (:content-provider.tag/uuid %)))
                                    (vec))]
           (assoc group :content-provider.tag-group/values filtered-values)))
       tag-groups))

(defn tag-group-component [tag-group classes name]
  (let [expanded? (r/atom false)]
    (fn []
      (when (seq tag-group)
        [:div {:class (:tag-group-container classes)}
         [text/caption-30 :p {:style {:margin-bottom (gs [:spacing :p4])}}
          (str name " Selected: " (count tag-group))]
         (for [tag-name (take (if @expanded? (count tag-group) 3) (sort tag-group))]
           ^{:key tag-name}
           [text/caption-20 :p {:style {:font-family (gs [:font-family :open-sans])
                                        :margin (gs [:spacing :p0])}} tag-name])
         (when (> (count tag-group) 3)
           [:button {:class (:show-more-show-less-button classes)
                     :on-click #(swap! expanded? not)}
            (if @expanded? "Show less" "Show more")])]))))

(defn tag-group-components [tag-groups names classes]
  (into
   [:<>]
   (map (fn [tag-group name]
          ^{:key name}
          [tag-group-component tag-group classes name])
        tag-groups
        names)))

(defn- render-boost-card
  [{:keys [boost curriculum-uuid activity-uuid learning-objectives activity-valid-tags]}]
  (let [{boost-name :name
         boost-uuid :uuid
         system-type :system-type
         boost-type :boost-type
         seconds :seconds
         show-stats :show-stats
         show-related-resources :show-related-resources
         questionnaire-uuid :questionnaire-uuid
         resource-uuids :resource-uuids
         learning-objective-uuid :learning-objective-uuid
         sent :sent
         opened :opened
         clicked :clicked
         published :published
         processing-stats :processing-stats
         valid-tags :valid-tags}
        boost

        filtered-activity-valid-tags (filter-tag-groups activity-valid-tags valid-tags)

        tag-groups (extract-tag-groups filtered-activity-valid-tags)

        {:keys [sent-count
                queued-count
                unsubscribed-count
                previously-engaged-count]
         :or {previously-engaged-count 0}}
        processing-stats

        {:keys [label]}
        (->> learning-objectives
             (filter (fn [{lo-uuid :uuid}] (= lo-uuid learning-objective-uuid)))
             (first))]
    [:div {:class (:boost-card-container classes)}
     [:div {:class (:boost-card-title-container classes)}
      [:div {:style {:display :flex
                     :margin-bottom (gs [:spacing :p8])}}
       [text/caption-10 :p
        {:style {:margin-right (gs [:spacing :p20])
                 :color (gs [:colors :gray-scale-400])}}
        (if (= system-type "immediate")
          "Immediately sent after publishing."
          (str "Sent "
               (seconds->time-str seconds)
               " after the learner has "
               (case boost-type
                 "trigger-when-activity-started" "started"
                 "trigger-when-activity-completed" "completed"

                 (js/console.error "Boost type not supported"))
               " the activity."))]
       [lo-pill/render {:label label}]]

      [:div {:class (:publish-details-container classes)}
       [text/caption-10 :p
        {:style {:color (gs [:colors :gray-scale-400])}}
        (ku/model->cms-time boost)]

       [is-live-pill/render
        published (if (= system-type "immediate") "Sent" "Live")]]]

     [:div {:class (:boost-card-name-line classes)}
      [text/title-30 :h4 boost-name]
      (when (some? system-type)
        [text/caption-10 :p
         {:style {:margin-left (gs [:spacing :p4])
                  :color (gs [:colors :gray-scale-400])}}
         (str "(" system-type ")")])]

     [:div {:class (:boost-details-line classes)}
      [:div
       [:div {:class (:processing-stats-container classes)}
        [text/caption-20 :p "Sent to:"]
        [text/caption-30 :p sent-count]

        (when-not (= system-type "immediate")
          [:<>
           [text/caption-20 :p "| Scheduled to receive:"]
           [text/caption-30 :p queued-count]])

        (when-not (= system-type "immediate")
          [:<>
           [text/caption-20 :p "| Excluded learners:"]
           [text/caption-30 :p previously-engaged-count]])

        [text/caption-20 :p "| Unsubscribed:"]
        [text/caption-30 :p unsubscribed-count]]
              
       [tag-group-components
        tag-groups
        (map :content-provider.tag-group/name filtered-activity-valid-tags)
        classes]

       [:div {:class (:boost-feature-toggle-container classes)}
        [render-feature-toggle
         {:label "Questionnaire"
          :enabled (some? questionnaire-uuid)}]
        [render-feature-toggle
         {:label "Learner Performance"
          :enabled (true? show-stats)}]
        [render-feature-toggle
         {:label "Related Resources"
          :enabled (and (boolean (seq resource-uuids))
                        (true? show-related-resources))}]]]

      [:div {:class (:click-rate-and-cta-container classes)}
       [render-email-stat {:label "Open Rate"
                           :stat opened
                           :total (:users sent)}]
       [render-email-stat {:label "Click Rate"
                           :stat clicked
                           :total (:users sent)}]

       [link/secondary-anchor-button
        {:href (aru/admin-path :edit-activity-boost
                               :curriculum-uuid curriculum-uuid
                               :activity-uuid activity-uuid
                               :boost-uuid boost-uuid)
         :style {:margin-left (gs [:spacing :p40])}}
        (if (and (= system-type "immediate")
                 (true? published))
          "View"
          "Edit")]]]]))

(defn- render-boost-list
  [{:keys [boosts curriculum-uuid activity-uuid learning-objectives]}]
  (let [activity
        (re-frame/subscribe [::kyber-db/get-record
                             {:model :activity
                              :ident activity-uuid}])

        activity-valid-tags (:activity/valid-tags @activity)]
  [:ul
   (for [{boost-uuid :uuid :as boost} boosts]
     ^{:key boost-uuid}
     [render-boost-card {:boost boost
                         :learning-objectives learning-objectives
                         :curriculum-uuid curriculum-uuid
                         :activity-uuid activity-uuid
                         :activity-valid-tags activity-valid-tags}])]))

(defn- render-no-emails-present [{:keys [title
                                         body
                                         activity-uuid
                                         curriculum-uuid]}]
  [:div {:class (:empty-boost-section-container classes)}
   [text/title-30 :p
    {:class (:empty-boost-section-title classes)}
    title]
   [text/body-20 :p
    {:class (:empty-boost-section-message classes)}
    body]
   [render-new-boost-button {:title title
                             :curriculum-uuid curriculum-uuid
                             :activity-uuid activity-uuid}]])

(defn- render-boosts-section
  [{:keys [boosts learning-objectives activity-uuid curriculum-uuid]}]
  [:div {:class (:boost-section-list-container classes)}
   [render-boost-list
    {:boosts (sort-by #(= (:boost-type %)
                          "trigger-when-activity-started")
                      (sort-by :seconds boosts))
     :curriculum-uuid curriculum-uuid
     :learning-objectives learning-objectives
     :activity-uuid activity-uuid}]])

(defn- render-boosts
  [_]
  (let [boosts (re-frame/subscribe [:boosts])
        learning-objectives (re-frame/subscribe [:learning-objectives])
        reengagement-boosts (filter #(= "reengagement" (:category %)) @boosts)
        user-gen-boosts (filter #(not= "reengagement" (:category %)) @boosts)]
    (fn [{:keys [curriculum-uuid activity-uuid]}]
      (let [is-super-user
            (re-frame/subscribe [:authentication/has-access?
                                 :is-super-user])]
        [:div
         [render-boost-header]
         [render-boost-description @is-super-user]
         (when (false? @is-super-user)
           [render-admin-reengagement-copy])

         [:div {:style {:margin :auto
                        :max-width "1200px"}}
          (if (pos? (count user-gen-boosts))
            [:<>
             [render-boost-list-header {:curriculum-uuid curriculum-uuid
                                        :activity-uuid activity-uuid}]
             [render-boosts-section {:boosts user-gen-boosts
                                     :learning-objectives @learning-objectives
                                     :curriculum-uuid curriculum-uuid
                                     :activity-uuid activity-uuid}]]
            [render-no-emails-present
             {:title "Create Activity Boosts"
              :curriculum-uuid curriculum-uuid
              :activity-uuid activity-uuid
              :body (str "Boosts are designed to create additional learning "
                         "opportunities for learners following their "
                         "engagement with an activity. Boosts may include new "
                         "content, related resources, and/or "
                         "questions/follow-up tests that help drive learners "
                         "to reflect on prior learning, to bend the "
                         "forgetting curve, and/or to generate additional data "
                         "demonstrating the impact of your intervention.")}])

          [text/caption-20 :p
           {:class (:reengagement-boost-message classes)}
           (str "In addition to the boosts you create, the system "
                "automatically generates re-engagement notifications for "
                "learners who have started the activity, but not completed "
                "viewing the video; as well as learners who have completed the "
                "activity, but not completed the post-test.")]

          (when (and (true? @is-super-user) (pos? (count reengagement-boosts)))
            [:<>
             [:div {:class (:admin-content-container classes)}
              [text/title-30 :p {:class (:admin-content-title classes)}
               "Re-engagement Boosts"]
              [render-boosts-section {:boosts reengagement-boosts
                                      :learning-objectives @learning-objectives
                                      :curriculum-uuid curriculum-uuid
                                      :activity-uuid activity-uuid}]]])]]))))

(defn render
  [{:keys [params resource-status]}]
  (if (ku/all-resources-loaded? resource-status)
    [:div {:class (:container classes)}
     [render-boosts params]]
    [loading/render
     {:loading-message "Please wait while we fetch your boosts"}]))
